import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { colors } from "../../style";
import {
  Card,
  errorMessage,
  Icon,
  LinkButton,
  PillButton,
  TextInput,
  ui,
} from "../ui";

const PROMPTS = [
  "What are the action items and who owns them?",
  "What decisions were made?",
  "Write a follow-up email to the attendees",
];

// ask: a mutation whose mutateAsync(question) resolves to the answer
// (useAskMeeting for meetings). renderAnswer/copyText cover non plain-text
// answers.
export function AskTab({
  ask,
  prompts = PROMPTS,
  subject = "meeting",
  renderAnswer = (text) => <AnswerText text={text} />,
  copyText = (answer) => answer,
}) {
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState("");
  const nextId = useRef(0);

  const submit = async (q) => {
    const question = q.trim();
    if (!question || ask.isLoading) return;
    const id = nextId.current++;
    const update = (change) =>
      setHistory((h) =>
        h.map((qa) => (qa.id === id ? { ...qa, ...change } : qa)),
      );
    setHistory((h) => [{ id, question, answer: null }, ...h]);
    setDraft("");
    try {
      update({ answer: await ask.mutateAsync(question) });
    } catch (e) {
      update({
        error: errorMessage(e, "Sidekick couldn't answer that. Try again."),
      });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {prompts.map((p) => (
          <PillButton
            key={p}
            disabled={ask.isLoading}
            onClick={() => submit(p)}
          >
            {p}
          </PillButton>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(draft);
        }}
        style={{ display: "flex", gap: "8px" }}
      >
        <TextInput
          value={draft}
          maxLength={2000}
          aria-label={`Ask about this ${subject}`}
          placeholder={`Ask anything about this ${subject}`}
          onChange={(e) => setDraft(e.target.value)}
          style={{ background: colors.surface }}
        />
        <button
          type="submit"
          title="Ask"
          aria-label="Ask"
          disabled={!draft.trim() || ask.isLoading}
          style={{
            flex: "none",
            width: "40px",
            borderRadius: "9px",
            border: 0,
            background: colors.teal,
            color: colors.onPrimary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            opacity: !draft.trim() || ask.isLoading ? 0.5 : 1,
          }}
        >
          <Icon name="arrowUp" size={18} />
        </button>
      </form>
      {history.map((qa) => (
        <Card key={qa.id} gap={8}>
          <div
            style={{ fontWeight: 700, color: colors.text, fontSize: "14px" }}
          >
            {qa.question}
          </div>
          {qa.error ? (
            <p style={ui.error}>{qa.error}</p>
          ) : qa.answer === null ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {[90, 75, 60].map((w) => (
                <span
                  key={w}
                  className="sk-skeleton"
                  style={{
                    display: "block",
                    height: "10px",
                    width: `${w}%`,
                    borderRadius: "5px",
                    background: colors.raised,
                  }}
                />
              ))}
            </div>
          ) : (
            <>
              {renderAnswer(qa.answer)}
              <LinkButton
                style={{ alignSelf: "flex-start" }}
                onClick={() =>
                  // navigator.clipboard is missing outside secure contexts
                  (
                    navigator.clipboard?.writeText(copyText(qa.answer)) ??
                    Promise.reject()
                  ).then(
                    () => toast("Copied to clipboard", { type: "success" }),
                    () => toast("Couldn't copy the answer", { type: "error" }),
                  )
                }
              >
                <Icon name="copy" size={12} />
                Copy
              </LinkButton>
            </>
          )}
        </Card>
      ))}
    </div>
  );
}

// The API answers in plain text: blank-line paragraphs and "- " bullets
function AnswerText({ text }) {
  const blocks = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    const bullet = /^[-•*]\s+/.test(line);
    const last = blocks[blocks.length - 1];
    if (!line) continue;
    if (bullet) {
      const item = line.replace(/^[-•*]\s+/, "");
      if (last?.list) last.items.push(item);
      else blocks.push({ list: true, items: [item] });
    } else {
      blocks.push({ list: false, text: line });
    }
  }
  const body = { margin: 0, fontSize: "14px", lineHeight: 1.6 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {blocks.map((b, i) =>
        b.list ? (
          <ul key={i} style={{ ...body, paddingLeft: "18px" }}>
            {b.items.map((item, j) => (
              <li key={j}>{item}</li>
            ))}
          </ul>
        ) : (
          <p key={i} style={body}>
            {b.text}
          </p>
        ),
      )}
    </div>
  );
}
